import { ref, toValue } from 'vue';
import i18n from '@/i18n';
import { cloneDeep } from 'lodash';
import { DateTime } from 'luxon';
import { helpers, required, minValue } from '@vuelidate/validators';
import { LANG_ENGLISH, LANG_FRENCH, URL_FLIGHT_EN, URL_FLIGHT_FR, INCENTIVE_GROUP_TYPE, PACKAGE_LAND_ONLY, LAND_ONLY_PACKAGE, PACKAGE, LAND_ONLY, HOTEL_ONLY, AIR_ONLY, CRUISE, MDT } from '@/constants';
import { getEnvVar } from '@/utils/env';
import { formatDate } from '@/utils/dateUtils';
import { getTextVariant } from '@/utils/stringUtils';
import { useHotelStore } from '@/store/HotelStore';
import { useRoomsStore } from '@/store/RoomsStore';
import { UserInfo } from '@/modules/user/UserInfo';
import { useUserInfo } from '@/modules/user/useUserInfo';
import { FilterMatchMode, FilterService } from '@primevue/core/api';

export function useTabComposable() {
  const BASE_URL = getEnvVar('VITE_CDN_BASE_URL');
  const loadingIcon = `${BASE_URL}/assets/images/loading.png`;
  const currentUser = ref<UserInfo>();
  let isAfterDate: boolean = false;

  const hotelRoomPayload = {
    header: 'FITGROUPVERIF-RQ',
    tourOp: 'VAC',
    net: 'Y',
    createQuote: 'N',
    branch: 'VA',
    groupEvent: 'LE'
  };

  const getUserInfo = async () => {
    currentUser.value = await useUserInfo().getUserInfo();
  };
  getUserInfo();

  const getUserParam = () => {
    return {
      email: toValue(currentUser)?.email,
      user: toValue(currentUser)?.name,
      phone: toValue(currentUser)?.phone,
      role: toValue(currentUser)?.role,
      agency: toValue(currentUser)?.agency,
      agencyChain: toValue(currentUser)?.agencyChain,
      agencyPhone: toValue(currentUser)?.agencyPhone,
      agencyEmail: toValue(currentUser)?.agencyEmail,
      region: toValue(currentUser)?.agencyProvince
    };
  };

  const filterHotel = (event, hotelList) => {
    if (event.originalEvent?.type === 'focus') {
      return [...hotelList];
    } else if (!event.query.trim().length) {
      return [...hotelList];
    } else {
      const newFilteredDestination = [];
      const query = event.query;
      const filteredItems = FilterService.filter(hotelList, ['name'], query, FilterMatchMode.CONTAINS);
      if (filteredItems?.length) {
        newFilteredDestination.push(...filteredItems);
      }
      return newFilteredDestination;
    }
  };

  const roomCategoryFetchingModalControl = ref({
    visible: false,
    isFetching: false
  });

  const depDateForm = ref('');

  const updateRoomCategoryFetchingModalControl = (visible, isFetching) => {
    roomCategoryFetchingModalControl.value.visible = visible;
    roomCategoryFetchingModalControl.value.isFetching = isFetching;
  };

  const hotelStore = useHotelStore();

  const renderNewPayload = (payload, dateSelected) => {
    const dateRet = DateTime.fromISO(dateSelected).plus({ days: payload.duration || 0 });
    return {
      ...payload,
      dateDep: formatDate(dateSelected, 'yyyyLLdd', false),
      dateRet: formatDate(dateRet, 'yyyyLLdd')
    };
  };

  const filterDatesInRange = (listDates: any, minVal: any, maxVal: any) => {
    const localDateMin = DateTime.fromJSDate(minVal).toUTC();
    const min = localDateMin.startOf('day').toMillis();

    const max = maxVal ? DateTime.fromJSDate(maxVal).toUTC().endOf('day').toMillis() : null;

    return listDates.filter((item: any) => {
      const itemDate = DateTime.fromISO(item.date).startOf('day').toMillis();
      return itemDate >= min && (!max || itemDate <= max);
    });
  };

  const getDateSelect = (listDate: any, onOpenModalSelectDate?: any) => {
    return new Promise((resolve) => {
      onOpenModalSelectDate(listDate, (selectedDate: any) => {
        resolve(selectedDate);
      });
    });
  };

  const packageLandOnlyCall = (payload, excludeHotelRoom, onOpenModalSelectDate, handleOpenModalError, payloadPackage, limitData?) => {
    updateRoomCategoryFetchingModalControl(true, true);
    hotelStore.updateRoomLoading(true);
    depDateForm.value = payload?.dateDep;
    return hotelStore
      .getRoomCategoryByPackagePlus(payload, excludeHotelRoom, payloadPackage)
      .then(async (response) => {
        hotelStore.updateRoomLoading(false);
        if (response.data?.[0].room) {
          updateRoomCategoryFetchingModalControl(false, false);
          return response;
        } else if (response.data?.[0]?.date) {
          updateRoomCategoryFetchingModalControl(false, false);
          const listDate = await filterDatesInRange(response.data, limitData.min, limitData.max);
          if (listDate.length) {
            const dateSelected = await getDateSelect(listDate, onOpenModalSelectDate);
            return await packageLandOnlyCall(renderNewPayload(payload, dateSelected), excludeHotelRoom, onOpenModalSelectDate, handleOpenModalError, payloadPackage, limitData);
          } else {
            handleOpenModalError();
            return [];
          }
        } else {
          return landOnlyCall(payload, excludeHotelRoom);
        }
      })
      .catch(() => {
        return landOnlyCall(payload, excludeHotelRoom);
      });
  };

  const landOnlyPackageCall = async (payload, excludeHotelRoom, onOpenModalSelectDate, handleOpenModalError, payloadPackage) => {
    updateRoomCategoryFetchingModalControl(true, true);
    hotelStore.updateRoomLoading(true);
    depDateForm.value = payload?.dateDep;
    return hotelStore
      .getHotelRoomCategory(payload, excludeHotelRoom)
      .then((response) => {
        hotelStore.updateRoomLoading(false);
        if (response.data?.length) {
          updateRoomCategoryFetchingModalControl(false, false);
          return response;
        } else {
          return packageCall(payload, excludeHotelRoom, onOpenModalSelectDate, handleOpenModalError, payloadPackage);
        }
      })
      .catch(() => {
        return packageCall(payload, excludeHotelRoom, onOpenModalSelectDate, handleOpenModalError, payloadPackage);
      });
  };

  const packageCall = async (payload, excludeHotelRoom, onOpenModalSelectDate, handleOpenModalError, payloadPackage, limitData?) => {
    updateRoomCategoryFetchingModalControl(true, true);
    hotelStore.updateRoomLoading(true);
    depDateForm.value = payload?.dateDep;
    return hotelStore
      .getRoomCategoryByPackagePlus(payload, excludeHotelRoom, payloadPackage, true)
      .then(async (response) => {
        hotelStore.updateRoomLoading(false);
        if (response.data.error) {
          updateRoomCategoryFetchingModalControl(true, false);
          return response;
        }
        if (response.data?.[0]?.room) {
          updateRoomCategoryFetchingModalControl(false, false);
          return response;
        } else if (response.data?.[0]?.date) {
          updateRoomCategoryFetchingModalControl(false, false);
          const listDate = await filterDatesInRange(response.data, limitData.min, limitData.max);
          if (listDate.length) {
            const dateSelected = await getDateSelect(listDate, onOpenModalSelectDate);
            return await packageCall(renderNewPayload(payload, dateSelected), excludeHotelRoom, onOpenModalSelectDate, handleOpenModalError, payloadPackage, limitData);
          } else {
            handleOpenModalError();
            return [];
          }
        }
        return response;
      })
      .catch((error) => {
        updateRoomCategoryFetchingModalControl(true, false);
        return error;
      });
  };

  const landOnlyCall = async (payload, excludeHotelRoom) => {
    updateRoomCategoryFetchingModalControl(true, true);
    hotelStore.updateRoomLoading(true);
    depDateForm.value = payload?.dateDep;
    return hotelStore
      .getHotelRoomCategory(payload, excludeHotelRoom)
      .then((response) => {
        hotelStore.updateRoomLoading(false);
        if (response.data?.length) {
          updateRoomCategoryFetchingModalControl(false, false);
        } else {
          updateRoomCategoryFetchingModalControl(true, false);
        }
        return response;
      })
      .catch((error) => {
        updateRoomCategoryFetchingModalControl(true, false);
        return error;
      });
  };

  const callRoomCategoryPreReg = async (payload: any, payloadPackage: any) => {
    const preRegPayload = {
      ...payload,
      productCode: [payload.productCode],
      gatewayDest: payloadPackage?.destDep,
      header: 'TTSGROUPVERIF-RQ'
    };
    updateRoomCategoryFetchingModalControl(true, true);
    hotelStore.updateRoomLoading(true);
    return hotelStore
      .getHotelRoomCategoryPreReg(preRegPayload)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      })
      .finally(() => {
        updateRoomCategoryFetchingModalControl(false, false);
        hotelStore.updateRoomLoading(false);
      });
  };

  const handleStandardRoom = (payload) => {
    depDateForm.value = payload?.dateDep;
    const standardRoom = [
      {
        destinationName: '',
        hotelName: '',
        room: 'STDRM',
        roomDescription: 'Standard Room'
      }
    ];
    hotelStore.updateHotelRoomCategory(payload.productCode, standardRoom);
    return {
      data: standardRoom
    };
  };

  const checkIsAfterDate346 = (isAfterDate346: boolean) => {
    isAfterDate = isAfterDate346;
  };

  const getRoomCategoriesData = async (payload, hotelSelected, excludeHotelRoom?, gateway?, onOpenModalSelectDate?, handleOpenModalError?, limitData?) => {
    const groupDes = hotelSelected?.groupDes?.join();
    const payloadPackage = {
      branch: groupDes || hotelSelected?.destCode,
      productCode: hotelSelected?.code,
      destDep: gateway?.code
    };
    const destCode = hotelSelected.hCode?.substring(0, 3);
    payload.destDep = destCode;

    const roomCategoryCallOrder = limitData?.isPackageForm ? hotelSelected.roomCategoryCallOrder : hotelSelected.roomCategoryCallOrderHotel;

    if (isAfterDate) {
      return handleResultFormAPI(payload, excludeHotelRoom, handleOpenModalError, onOpenModalSelectDate, payloadPackage);
    } else {
      switch (roomCategoryCallOrder) {
        case PACKAGE_LAND_ONLY:
          return packageLandOnlyCall(payload, excludeHotelRoom, onOpenModalSelectDate, handleOpenModalError, payloadPackage, limitData);
        case LAND_ONLY_PACKAGE:
          return landOnlyPackageCall(payload, excludeHotelRoom, onOpenModalSelectDate, handleOpenModalError, payloadPackage);
        case PACKAGE:
          return packageCall(payload, excludeHotelRoom, onOpenModalSelectDate, handleOpenModalError, payloadPackage, limitData);
        case LAND_ONLY:
          return landOnlyCall(payload, excludeHotelRoom);
        default:
          return handleStandardRoom(payload);
      }
    }
  };

  const formatAirport = (airportList, language) => {
    return airportList.map((airport) => {
      const airportData = cloneDeep(airport);
      const airportNameEn = airport.airportName ?? airport.airportNameEn;
      const countryNameEn = airport.countryName ?? airport.countryNameEn;
      airportData.airportNameLabel = airport.airportName;
      airportData.airportNameLabelFr = airport.airportNameFR;
      airportData.airportName = getTextVariant(language, airportNameEn, airport.airportNameFR);
      airportData.countryName = getTextVariant(language, countryNameEn, airport.countryNameFr);
      airportData.name = getTextVariant(language, airport.name, airport.nameFr);
      airportData.region = getTextVariant(language, airport.region, airport.regionFr);
      airportData.province = getTextVariant(language, airport.province, airport.provinceFr);
      airportData.dataSelected = `${getTextVariant(language, airportNameEn, airport.airportNameFR)} (${airport.airportCode})`;
      return airportData;
    });
  };

  const handleResultFormAPI = async (payload: any, excludeHotelRoom: any, handleOpenModalError: any, onOpenModalSelectDate: any, payloadPackage: any) => {
    const landOnlyCallData = await landOnlyCall(payload, excludeHotelRoom);
    if (!landOnlyCallData.data?.error && landOnlyCallData.data?.length) {
      return landOnlyCallData;
    }
    const packageCallData = await packageCall(payload, excludeHotelRoom, onOpenModalSelectDate, handleOpenModalError, payloadPackage);
    if (!packageCallData.data?.error && packageCallData.data?.length) {
      return packageCallData;
    }
    const preRegCallData = await callRoomCategoryPreReg(payload, payloadPackage);
    if (!preRegCallData?.data?.error && preRegCallData?.data?.length) {
      return preRegCallData;
    }
    handleOpenModalError(true);
    return null;
  };

  const getUserQuote = (quote) => {
    return {
      email: quote?.email || '',
      user: quote?.user || '',
      phone: quote?.phone || '',
      role: quote?.role || '',
      agency: quote?.agency || '',
      agencyChain: quote?.agencyChain || '',
      agencyPhone: quote?.agencyPhone || '',
      agencyEmail: quote?.agencyEmail || '',
      region: quote?.region || ''
    };
  };

  const handleRedirectFlight = () => {
    window.open(i18n.global.locale.value === 'fr' ? URL_FLIGHT_FR : URL_FLIGHT_EN, '_blank');
  };

  const filterRoomOptions = (roomList, roomsSelected, isPackageForm) => {
    const roomsStore = useRoomsStore();

    if (!roomList && !isPackageForm) {
      return roomsStore.selectRooms;
    }
    if (!roomList && isPackageForm) {
      return [];
    }
    return roomList.filter((room) => !roomsSelected.map((selected) => selected.room?.room).includes(room.room));
  };
  const disableAddRoom = (listRoom, roomsSelected) => {
    if (filterRoomOptions(listRoom, roomsSelected)?.length > 0 && listRoom?.length > 0) {
      return false;
    } else {
      return !listRoom?.length || listRoom?.length < 3;
    }
  };

  const formatPackageHotel = (hotels: any) => {
    const hotelsTemp = [];
    hotels.forEach((hotel) => {
      const roomsTemp = [];
      hotel.rooms.forEach((room) => {
        const roomForm = {
          room: renderRoomName(room?.room, LANG_ENGLISH, false),
          roomFr: renderRoomName(room?.room, LANG_FRENCH, false),
          longName: renderRoomName(room?.room, LANG_ENGLISH, true),
          longNameFr: renderRoomName(room?.room, LANG_FRENCH, true),
          code: room?.room.room,
          totalTax: 0
        };
        roomsTemp.push(roomForm);
      });
      const hotelForm = {
        hCode: hotel.hotel.hCode,
        hotel: hotel.hotel.name,
        code: hotel.hotel.code,
        hotelChain: hotel.hotel.chainName,
        adminMaxRoom: hotel.hotel.maxRooms,
        rooms: roomsTemp,
        adminInsta: hotel.hotel.insta,
        inBlackoutDate: hotel.hotel.inBlackoutDate
      };
      hotelsTemp.push(hotelForm);
    });
    return hotelsTemp;
  };

  const initPackageData = () => {
    return {
      gateway: '',
      destination: '',
      dateRange: undefined,
      duration: {},
      adults: 0,
      children: 0,
      numOfRooms: 0,
      hotels: [
        {
          hotel: '',
          rooms: [
            {
              room: ''
            }
          ]
        }
      ],
      additionalNote: ''
    };
  };

  const ruleValue = {
    $each: helpers.forEach({
      gateway: { required: helpers.withMessage(i18n.global.t('validate.required'), required) },
      destination: { required: helpers.withMessage(i18n.global.t('validate.required'), required) },
      duration: { required: helpers.withMessage(i18n.global.t('validate.required'), required) },
      dateRange: {
        required: helpers.withMessage(i18n.global.t('validate.required'), required),
        isNotZero: helpers.withMessage(i18n.global.t('validate.required'), (value) => value !== 0)
      },
      adults: { required: helpers.withMessage(i18n.global.t('validate.required'), required), minValue: helpers.withMessage(i18n.global.t('validate.min_value'), minValue(1)) },
      numOfRooms: {
        required: helpers.withMessage(i18n.global.t('validate.required'), required),
        minValue: helpers.withMessage(i18n.global.t('validate.min_value'), minValue(1))
      },
      hotels: {
        $each: helpers.forEach({
          hotel: { required: helpers.withMessage(i18n.global.t('validate.required'), required) },
          rooms: {
            $each: helpers.forEach({
              room: { required: helpers.withMessage(i18n.global.t('validate.required'), required) }
            })
          }
        })
      }
    })
  };

  const initHotelRoomPayload = (packageData, e, type, dateValue, durationDate, hCode) => {
    const payload = {
      dateDep: formatDate(packageData.dateRange, 'yyyyLLdd'),
      dateRet: '',
      duration: packageData.duration.name,
      destDep: '',
      productCode: hCode,
      language: i18n.global.locale.value.toUpperCase(),
      nbAdult: +packageData.adults,
      ...hotelRoomPayload
    };
    switch (type) {
      case 'date':
        payload.dateRet = formatDate(dateValue, 'yyyyLLdd');
        payload.destDep = packageData.destination.airportCode;
        break;
      case 'duration':
        payload.dateRet = formatDate(durationDate, 'yyyyLLdd');
        payload.destDep = e.destination.airportCode;
        break;
      case 'adult':
        payload.dateRet = formatDate(DateTime.fromJSDate(new Date(packageData.dateRange)).plus({ days: packageData.duration.name || 0 }), 'yyyyLLdd');
        payload.destDep = packageData.destination.airportCode;
        break;
      default:
        break;
    }
    return payload;
  };

  const checkAdultPackages = (packageList: any, minimumAdult: number = 0) => {
    const adultPackages = packageList.reduce((result: any, item: any) => {
      if (!result[item.destCode]) {
        result[item.destCode] = {
          destCode: item.destCode,
          adtNo: 0
        };
      }
      result[item.destCode].adtNo += item.adtNo;
      return result;
    }, {});

    return Object.values(adultPackages).some((pacakge: any) => pacakge.adtNo < minimumAdult);
  };

  const checkTotalPassenger = (packageList: any, minimumAdult: number = 0) => {
    const adultPackages = packageList.reduce((result: any, item: any) => {
      if (!result[item.destCode]) {
        result[item.destCode] = {
          destCode: item.destCode,
          chdNo: 0,
          adtNo: 0
        };
      }
      result[item.destCode].adtNo += item.adtNo;
      result[item.destCode].chdNo += item.chdNo;
      return result;
    }, {});

    return Object.values(adultPackages).some((pacakge: any) => pacakge.adtNo + pacakge.chdNo < minimumAdult);
  };

  const checkTotalNumberOfRooms = (packageList: any, minimumRoom: number = 0) => {
    const roomPackages = packageList.reduce((result: any, item: any) => {
      result[item.destCode] ??= {
        destCode: item.destCode,
        numOfRooms: 0
      };
      result[item.destCode].numOfRooms += item.numOfRooms;
      return result;
    }, {});
    return Object.values(roomPackages).some((pacakge: any) => pacakge.numOfRooms < minimumRoom);
  };

  const checkTotalAdultAndRoom = (packageList: any[]) => {
    const calculate = packageList.reduce((result, item) => {
      if (!result[item.destCode]) {
        result[item.destCode] = {
          destCode: item.destCode,
          adtNo: 0,
          chdNo: 0,
          numOfRooms: 0
        };
      }
      result[item.destCode].adtNo += item.adtNo;
      result[item.destCode].chdNo += item.chdNo;
      result[item.destCode].numOfRooms += item.numOfRooms;
      return result;
    }, {});

    const total = Object.values(calculate).filter((item: any) => {
      return {
        adtNo: item.adtNo,
        chdNo: item.chdNo,
        numOfRooms: item.numOfRooms
      };
    });
    return total;
  };

  const checkAdultEachPackages = (packageList: any[], minimumAdult: number = 0) => {
    return packageList?.map((packageItem: any) => packageItem.adtNo + packageItem.chdNo).some((total: number) => total < minimumAdult);
  };

  const checkRoomsEachPackage = (packageList: any[], minimumRooms: number = 0): boolean => {
    return packageList?.some((packageItem: any) => packageItem.numOfRooms < minimumRooms);
  };

  const renderPackageTemp = (m, durationDate, index, dateValue) => {
    const formatDepDate = new Date(DateTime.fromISO(formatDate(m.dateRange)));
    const formatRetDate = new Date(DateTime.fromISO(formatDate(durationDate[index] ? durationDate[index] : dateValue[index])));
    return {
      destCode: m.destination.code,
      gateCode: m.gateway.code,
      gate: m.gateway?.airportNameLabel,
      gateFr: m.gateway?.airportNameLabelFr,
      dest: `${m.destination.name} (${m.destination.country})`,
      destFr: `${m.destination.nameFr} (${m.destination.countryFr})`,
      airportCode: m.destination.airportCode,
      depDate: formatDate(formatDepDate, 'yyyy-LL-dd HH:mm', true).replace(' ', 'T'),
      retDate: formatDate(formatRetDate, 'yyyy-LL-dd HH:mm', true).replace(' ', 'T'),
      duration: m.duration.name,
      numOfRooms: m.numOfRooms,
      adtNo: m.adults,
      chdNo: m.children,
      infNo: 1,
      direct: false,
      stops: 0,
      hotels: formatPackageHotel(m.hotels),
      additionalNote: m.additionalNote,
      brochure: m.brochure
    };
  };

  const validateDateTime = (date, groupType) => {
    if (groupType === INCENTIVE_GROUP_TYPE) {
      return false;
    } else {
      const currentDate = DateTime.now();
      const dateSelected = DateTime.fromISO(date);
      const daysDifference = dateSelected.diff(currentDate, 'days').days;
      return daysDifference > 900;
    }
  };

  const validateBlackoutDate = (hotels: any[]) => {
    return !hotels.some((hotel) => {
      return hotel.inBlackoutDate;
    });
  };

  const checkOverDateCruise = (startDate, cruise, groupType) => {
    const isValidCruise = (date) => {
      const validateCruise = validateDateTime(date, groupType);
      return validateCruise;
    };

    const isValidPreHotel = () => {
      const hasPreHotel = cruise?.preHotel?.length > 0;
      if (hasPreHotel) {
        return validateDateTime(cruise.preHotel[0].depDate, groupType);
      } else {
        return hasPreHotel;
      }
    };

    const isValidPostHotel = () => {
      const hasPostHotel = cruise?.postHotel?.length > 0;
      if (hasPostHotel) {
        return validateDateTime(cruise.postHotel[0].depDate, groupType);
      } else {
        return hasPostHotel;
      }
    };

    const isValidFlight = () => {
      const hasFlightPrices = cruise?.flights?.length > 0;
      if (hasFlightPrices) {
        return validateDateTime(cruise.flights[0].depDate, groupType);
      } else {
        return hasFlightPrices;
      }
    };

    return isValidCruise(DateTime.fromJSDate(startDate).toFormat('yyyy-LL-dd')) || isValidFlight() || isValidPreHotel() || isValidPostHotel();
  };

  const renderOriginQuote = (isWorksheetRequoteValue, quoteID) => {
    return isWorksheetRequoteValue ? quoteID : null;
  };

  const renderRoomName = (room: any, selectedLanguage: string, isLongName?: boolean) => {
    if (isLongName) {
      return getTextVariant(selectedLanguage, room?.roomDescription, room?.roomDescriptionFr);
    } else {
      return getTextVariant(selectedLanguage, room?.shortDesc, room?.shortDescFr);
    }
  };

  const getDefaultDate = (type: string, dateKey: string) => {
    switch (type) {
      case PACKAGE:
        return dateKey == 'startDateConfig' ? 45 : 346;
      case HOTEL_ONLY:
        return dateKey == 'startDateConfig' ? 60 : 900;
      case AIR_ONLY:
        return dateKey == 'startDateConfig' ? 60 : 900;
      case CRUISE:
        return dateKey == 'startDateConfig' ? 60 : 900;
      case MDT:
        return dateKey == 'startDateConfig' ? 60 : 346;
      default:
        return dateKey == 'startDateConfig' ? 45 : 900;
    }
  };

  const getQuoteDepartureData = (calendarData: any) => {
    const quoteData = {
      startDateConfig: [],
      endDateConfig: []
    };
    if (calendarData) {
      quoteData.startDateConfig = calendarData.startDateConfig || null;
      quoteData.endDateConfig = calendarData.endDateConfig || null;
    }
    return quoteData;
  };

  const getLimitDate = (typeId: string, limitDate: any, dateKey: string) => {
    let defaultVal = dateKey == 'startDateConfig' ? 45 : 900;
    if (limitDate[dateKey]) {
      defaultVal = getDefaultDate(limitDate[dateKey].quoteType, dateKey);
      for (const group of limitDate[dateKey]) {
        if (group.groupList.includes(typeId)) {
          return new Date(DateTime.utc().plus({ days: group.limit ? group.limit : defaultVal }));
        }
      }
    }
    return new Date(DateTime.utc().plus({ days: defaultVal }));
  };

  const handleSetLimit = (advancedForm: any, config: any) => {
    let maxOption = 1;
    let maxHotel = 1;
    let maxRoom = 1;
    let maxCruiseCategory = 1;
    if (advancedForm[0].groupType.includes('Incentive')) {
      maxOption = config?.incentiveMaxOptionPermitted;
      maxHotel = config?.incentiveMaxHotelPerOption;
      maxRoom = config?.incentiveMaxRoomPerHotel;
      maxCruiseCategory = config?.incentiveCruiseMaxCategory;
    } else {
      maxOption = config?.maxOptionPermitted;
      maxHotel = config?.maxHotelPerOption;
      maxRoom = config?.maxRoomPerHotel;
      maxCruiseCategory = config?.cruiseMaxCategory;
    }

    return { maxOption, maxHotel, maxRoom, maxCruiseCategory };
  };

  const removeFromEnd = (array: any[], count: number) => {
    if (array?.length > count) {
      array.splice(count);
    }
  };

  const handleCheckRoom = (rooms: any) => {
    return rooms.filter((roomItem: any) => roomItem.room && roomItem.room !== '');
  };

  const cleanEmptyRooms = (data: any[]) => {
    data.forEach((item: any) => {
      item.hotels?.forEach((hotel: any) => {
        if (!Array.isArray(hotel.rooms)) {
          return;
        }
        const rooms = hotel.rooms;
        if (rooms.length === 1) {
          return;
        }
        const filtered = handleCheckRoom(rooms);
        hotel.rooms = filtered.length > 0 ? filtered : [rooms[0]];
      });
    });
  };

  return {
    hotelRoomPayload,
    filterHotel,
    roomCategoryFetchingModalControl,
    updateRoomCategoryFetchingModalControl,
    getRoomCategoriesData,
    getUserParam,
    getUserQuote,
    formatAirport,
    loadingIcon,
    filterRoomOptions,
    disableAddRoom,
    handleRedirectFlight,
    formatPackageHotel,
    initPackageData,
    ruleValue,
    initHotelRoomPayload,
    checkAdultPackages,
    checkTotalAdultAndRoom,
    checkAdultEachPackages,
    checkRoomsEachPackage,
    renderPackageTemp,
    validateDateTime,
    checkOverDateCruise,
    renderOriginQuote,
    handleStandardRoom,
    landOnlyCall,
    packageCall,
    landOnlyPackageCall,
    packageLandOnlyCall,
    renderNewPayload,
    depDateForm,
    renderRoomName,
    checkIsAfterDate346,
    checkTotalPassenger,
    checkTotalNumberOfRooms,
    getDefaultDate,
    getQuoteDepartureData,
    getLimitDate,
    filterDatesInRange,
    getDateSelect,
    handleSetLimit,
    removeFromEnd,
    cleanEmptyRooms,
    validateBlackoutDate
  };
}
